// audioDetector — MediaPipe AudioClassifier (YAMNet) wrapper for the
// `mediapipe.audio_classifier` sub-feature.
//
// Listens to the candidate's microphone stream and watches for two patterns:
//
//   A. Sustained "speech" classification. YAMNet's "Speech" category fires
//      whenever someone is talking near the microphone. The candidate ISN'T
//      expected to talk during a coding test, so sustained speech for
//      `second_voice_seconds` (default 2s) is a strong signal that someone
//      else is in the room.
//
//   B. Wake-word patterns (heuristic). When YAMNet returns "Speech" AND
//      the candidate isn't actively typing, this MAY indicate they're
//      saying something like "Hey Siri, write Python code for two-sum".
//      Real wake-word detection would need a dedicated keyword spotter
//      (not in YAMNet's vocabulary), so for v1 we just heighten severity
//      for any speech-during-silence pattern. The flag's `wake_word_list`
//      threshold is stored but not used until a follow-up keyword spotter
//      ships.
//
// Audio capture:
//   Uses an AudioContext bound to the existing webcam MediaStream (which
//   includes an audio track when getUserMedia was called with `audio: true`).
//   A ScriptProcessorNode chunks raw audio into 1-second windows at 16kHz
//   for the classifier. ScriptProcessorNode is deprecated in spec but still
//   universally supported; switching to AudioWorklet is a follow-up.
//
// Off by default in the registry. Enabling it without a working microphone
// permission no-ops cleanly (no errors surfaced to the candidate).

import { loadAudioTasks, MODEL_URLS } from '../mediapipeLoader';
import { createDurationStateMachine } from '../stateMachine';

const FEATURE_AUDIO = 'mediapipe.audio_classifier';
const TARGET_SAMPLE_RATE = 16000;            // YAMNet expects 16kHz
const PROCESSOR_BUFFER_SIZE = 4096;          // power-of-two sample buffer
const CLASSIFY_WINDOW_SECONDS = 1.0;         // YAMNet classifies ~1-second clips
const MIN_SPEECH_CONFIDENCE = 0.5;           // confidence threshold for "Speech"

// YAMNet labels we treat as a "voice in the room" signal. Lower-case for
// case-insensitive matching against the model's returned category names.
const SPEECH_LIKE_LABELS = new Set([
  'speech',
  'conversation',
  'narration, monologue',
  'whispering',
  'shout',
  'yell',
  'child speech, kid speaking',
]);

const num = (obj, key, fallback) => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Build an audio detector.
 *
 * @param {object} opts
 * @param {MediaStream} opts.stream     Webcam stream with an audio track.
 *   Must have been obtained with `{ audio: true }` — otherwise the detector
 *   start() rejects with a clear error.
 * @param {(event: object) => void} opts.emit
 * @param {(key: string) => boolean} opts.getFlag
 * @param {(key: string) => object} opts.getThreshold
 */
export function createAudioDetector({
  stream,
  emit,
  getFlag,
  getThreshold,
}) {
  if (!stream) {
    throw new Error('[audioDetector] stream is required');
  }
  if (typeof emit !== 'function') {
    throw new Error('[audioDetector] emit must be a function');
  }
  if (typeof getFlag !== 'function' || typeof getThreshold !== 'function') {
    throw new Error('[audioDetector] getFlag and getThreshold must be functions');
  }

  let classifier = null;
  let audioCtx = null;
  let sourceNode = null;
  let processorNode = null;
  let running = false;
  let ready = false;
  let smSpeech = null;

  // Ring buffer that accumulates raw samples until we have a full
  // CLASSIFY_WINDOW_SECONDS-second window, then classifies and resets.
  const targetSamples = TARGET_SAMPLE_RATE * CLASSIFY_WINDOW_SECONDS;
  let buffer = new Float32Array(targetSamples);
  let bufferPos = 0;

  const ensureClassifier = async () => {
    if (classifier) return classifier;
    const { audio, modules } = await loadAudioTasks();
    const { AudioClassifier } = modules;

    classifier = await AudioClassifier.createFromOptions(audio, {
      baseOptions: {
        modelAssetPath: MODEL_URLS.audioClassifier,
      },
      // `audio` runningMode for one-shot classification on samples we feed.
      runningMode: 'AUDIO_CLIPS',
      maxResults: 5, // top-5 categories — plenty for our pattern matching
      scoreThreshold: 0.10,
    });
    return classifier;
  };

  const initStateMachines = () => {
    const thr = getThreshold(FEATURE_AUDIO);
    const sustainedSeconds = num(thr, 'second_voice_seconds', 2);

    smSpeech = createDurationStateMachine({
      warnAfterMs: sustainedSeconds * 1000,
      flagAfterMs: (sustainedSeconds + 3) * 1000,
      onWarn: () => {
        emit({
          feature_key: FEATURE_AUDIO,
          severity: 'medium',
          score_delta: 4,
          message: 'Voice detected — please ensure no one else is present',
          evidence: { stage: 'warn', pattern: 'sustained_speech' },
        });
      },
      onFlag: () => {
        emit({
          feature_key: FEATURE_AUDIO,
          severity: 'high',
          score_delta: 10,
          message: 'Sustained voice activity — possible second person in room',
          evidence: { stage: 'flag', pattern: 'sustained_speech' },
        });
      },
    });
  };

  // Classify one full window — returns true iff a "speech-like" category
  // came back above MIN_SPEECH_CONFIDENCE.
  const classifyWindow = async (samples) => {
    if (!classifier) return false;
    let results;
    try {
      // AudioClassifier.classify accepts Float32Array + sample rate.
      results = await classifier.classify(samples, TARGET_SAMPLE_RATE);
    } catch (err) {
      // Don't tear down — single-window failures are common during page
      // unfocus / mic glitches. Just skip this tick.
      // eslint-disable-next-line no-console
      console.warn('[audioDetector] classify failed:', err?.message || err);
      return false;
    }
    if (!Array.isArray(results) || results.length === 0) return false;
    const top = results[0]?.classifications?.[0]?.categories || [];
    return top.some((c) => {
      const name = (c.categoryName || '').toLowerCase().trim();
      return SPEECH_LIKE_LABELS.has(name) && c.score >= MIN_SPEECH_CONFIDENCE;
    });
  };

  // Handler invoked by the ScriptProcessorNode for each PROCESSOR_BUFFER_SIZE
  // chunk. We resample from the AudioContext's native rate to 16kHz using
  // simple linear-stride downsampling (good enough for speech classification).
  const onAudioChunk = (event) => {
    if (!running) return;
    const ch0 = event.inputBuffer.getChannelData(0);
    const inputRate = audioCtx.sampleRate;
    const ratio = inputRate / TARGET_SAMPLE_RATE;

    // Stride-resample directly into the ring buffer.
    let i = 0;
    while (i < ch0.length && bufferPos < buffer.length) {
      const sourceIdx = Math.floor(i / ratio);
      if (sourceIdx >= ch0.length) break;
      buffer[bufferPos++] = ch0[sourceIdx];
      // Step forward in input rate-corrected steps. Math is exact for
      // common rates (44.1 / 48 / 16) and "good enough" otherwise.
      i += Math.max(1, Math.round(ratio));
    }

    // If we just filled a window, classify it and reset the buffer.
    if (bufferPos >= buffer.length) {
      const windowCopy = buffer.slice(0, buffer.length);
      buffer = new Float32Array(targetSamples);
      bufferPos = 0;

      // Don't await — keep the audio pipeline pulling samples continuously
      // while classify runs in the background.
      void (async () => {
        if (!getFlag(FEATURE_AUDIO)) {
          if (smSpeech) smSpeech.reset();
          return;
        }
        const speechDetected = await classifyWindow(windowCopy);
        smSpeech.update(speechDetected);
      })();
    }
  };

  const start = async () => {
    if (running) return;

    // Verify the stream actually has an audio track — getUserMedia might
    // have been called with `audio: false`, in which case audio detection
    // can't work.
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks || audioTracks.length === 0) {
      throw new Error('[audioDetector] no audio track on stream');
    }

    await ensureClassifier();
    initStateMachines();

    // Cross-browser AudioContext.
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) {
      throw new Error('[audioDetector] AudioContext not supported');
    }
    audioCtx = new Ctor();
    sourceNode = audioCtx.createMediaStreamSource(stream);

    // ScriptProcessorNode is deprecated in spec but supported in every
    // shipping browser. AudioWorklet would be more correct but requires
    // an externally-hosted worklet file — out of scope for this batch.
    // TODO: migrate to AudioWorkletNode when we move detection out of the
    //       main thread.
    processorNode = audioCtx.createScriptProcessor(PROCESSOR_BUFFER_SIZE, 1, 1);
    processorNode.onaudioprocess = onAudioChunk;
    sourceNode.connect(processorNode);
    // ScriptProcessor needs a destination to run on most browsers, even if
    // we don't want playback. Routing through a gain=0 node mutes it.
    const muteGain = audioCtx.createGain();
    muteGain.gain.value = 0;
    processorNode.connect(muteGain).connect(audioCtx.destination);

    running = true;
    ready = true;
  };

  const stop = () => {
    running = false;
    ready = false;
    if (smSpeech) smSpeech.reset();
    try { processorNode?.disconnect(); } catch (_) { /* defensive */ }
    try { sourceNode?.disconnect(); } catch (_) { /* defensive */ }
    try { void audioCtx?.close(); } catch (_) { /* defensive */ }
    processorNode = null;
    sourceNode = null;
    audioCtx = null;
    if (classifier) {
      try { classifier.close(); } catch (_) { /* already disposed */ }
      classifier = null;
    }
  };

  const isReady = () => ready;

  return { start, stop, isReady };
}
